import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from "react-router";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Swal from 'sweetalert2';
import imgUrl from '../assets/images/imgUrl';
import { Configuration } from '../assets/data/config';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${Configuration.apiService.key}admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store admin data in localStorage
        localStorage.setItem('adminData', JSON.stringify(data.admin));
        localStorage.setItem('isAdminLoggedIn', 'true');
        
        // Swal.fire({
        //   title: 'Success',
        //   text: 'Admin logged in successfully!',
        //   icon: 'success',
        //   showConfirmButton: false,
        //   timer: 1500
        // });

        // Redirect to admin dashboard
        navigate('/admin/dashboard');
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: "hidden"
      }}
      className="LoginSection"
    >
      <Paper elevation={5} sx={{ p: 4, width: 400, textAlign: 'center', borderRadius: 2 }} className='UserSubSection'>
        {/* Logo */}
        <Box sx={{ mb: 2 }}>
          <img src={imgUrl.peaceMovImg} alt="Logo" width="100" style={{cursor:'pointer'}}  onClick={() => navigate("/")}/>
        </Box>

        {/* Heading */}
        <Typography variant="h5" fontWeight="600" mb={1} className='loginHeading'>
          ADMIN LOGIN
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          System Administrator Access
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          {/* Username Input */}
          <Typography sx={{ textAlign: "left", mb: 1 }}>Username</Typography>
          <TextField
            fullWidth
            placeholder='Enter username'
            type="text"
            required
            variant="outlined"
            margin="dense"
            size="small"
            sx={{ mb: 2 }}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />

          {/* Password Input */}
          <Typography sx={{ textAlign: "left", mb: 1 }}>Password</Typography>
          <TextField
            fullWidth
            placeholder="Enter password"
            type={showPassword ? 'text' : 'password'}
            required
            variant="outlined"
            margin="dense"
            size="small"
            sx={{ mb: 3 }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePassword}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Login Button */}
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={isLoading}
            sx={{
              mt: 2,
              background: 'linear-gradient(to right, #214F7D, #3A8139)',
              color: 'white',
              borderRadius: "50px",
              py: 1.5
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Login to Admin Panel'
            )}
          </Button>
        </form>

        {/* Back to Regular Login */}
        <Button
          fullWidth
          variant="outlined"
          sx={{
            mt: 2,
            borderColor: '#214F7D',
            color: '#214F7D',
            borderRadius: "50px"
          }}
          onClick={() => navigate('/login')}
        >
          Back to User Login
        </Button>
      </Paper>
    </Box>
  );
};

export default AdminLogin;
